import { useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';

// ── command-scope.v1 — Command Center Phase 1, packet A ──────────────────────
// (DOCKET-2026-10-21-04). The Plus backend answers both cost routes with this
// envelope; the browser renders it and never merges two scopes into one figure.

interface ScopeBlock {
  scope_kind: 'estate' | 'all_tenants' | 'tenant';
  subject_id: string | null;
  label: string;
}

interface CoverageWindow {
  start: string;
  end: string;
  days: number;
}

interface CoverageBlock {
  window: CoverageWindow | null;
  rows: number | null;
  complete: boolean | null;
  freshness_bound_s: number;
  note: string | null;
}

interface FailureBlock {
  kind: string;
  message: string;
  code: string | null;
  http_status: number | null;
  ref: string | null;
}

interface UnitBlock {
  kind: string;
  code: string | null;
  scale: number | null;
}

interface CommandScopeEnvelope<T> {
  schema_version: string;
  scope: ScopeBlock;
  status: 'live' | 'stale' | 'unavailable' | 'unknown' | 'off';
  observed_at: string | null;
  collected_at: string;
  source_ref: { system: string; route: string | null; revision: string | null; note: string | null };
  coverage: CoverageBlock;
  measurement_basis: string | null;
  unit: UnitBlock | null;
  data: T | null;
  last_good: { data: T; observed_at: string; collected_at: string } | null;
  failure: FailureBlock | null;
}

interface ProviderCostItem {
  provider: string;
  total_cost_usd: number;
  total_tokens: number;
  call_count: number;
}

interface RollupData {
  days: number;
  total_cost_usd: number;
  total_tokens: number;
  total_calls: number;
  by_provider: ProviderCostItem[];
  period_start?: string | null;
  period_end?: string | null;
}

interface DailyTrendItem {
  date: string;
  total_cost_usd: number;
  call_count: number;
  top_action?: string | null;
}

interface SummaryData {
  days: number;
  total_cost_usd: number;
  // Optional in the engine's summary: a tenant may have no budget set, and the
  // engine may report no daily average. Absent is absent, and it renders as
  // `—` — never as `$0.00`.
  budget_limit_usd?: number | null;
  avg_daily_cost_usd?: number | null;
  daily_trends: DailyTrendItem[];
}

// The two scopes this panel shows, labelled as the Phase 1 ruling requires.
// They are never one figure: the rollup covers every tenant, the summary
// covers the caller's own tenant, and nothing is summed across them.
const ALL_TENANTS_LABEL = 'All tenants, estimated provider cost, 30 days, coverage: UsageRecord rows';
const THIS_TENANT_LABEL = 'This tenant';

/**
 * One scope's honest state. A rejected read is `unavailable` with the error —
 * it never borrows the other scope's figures and never becomes a zero.
 */
type ScopeRead<T> =
  | { kind: 'loading' }
  | { kind: 'facts'; envelope: CommandScopeEnvelope<T> }
  | { kind: 'unavailable'; envelope: CommandScopeEnvelope<T> | null; message: string };

function readScope<T>(
  result: PromiseSettledResult<CommandScopeEnvelope<T>>,
  fallbackMessage: string,
): ScopeRead<T> {
  if (result.status === 'rejected') {
    return {
      kind: 'unavailable',
      envelope: null,
      message: errorMessage(result.reason, fallbackMessage),
    };
  }
  const envelope = result.value ?? null;
  if (!envelope) {
    return { kind: 'unavailable', envelope: null, message: fallbackMessage };
  }
  // A `stale` read is "successful but old" (A §2.2): it carries its facts, so
  // it is rendered — with its own `status` and `observed_at`, never silently as
  // a live block.
  if ((envelope.status === 'live' || envelope.status === 'stale') && envelope.data !== null) {
    return { kind: 'facts', envelope };
  }
  return {
    kind: 'unavailable',
    envelope,
    message: envelope.failure?.message ?? fallbackMessage,
  };
}

/**
 * The operator-readable message of a failed read.
 *
 * A refusal comes back as the contract's non-envelope body
 * (`{"error": {...}}`), which arrives here as raw JSON text; the operator sees
 * the message inside it, not the JSON.
 */
function errorMessage(reason: unknown, fallback: string): string {
  if (!(reason instanceof Error)) return fallback;
  const raw = reason.message;
  try {
    const parsed = JSON.parse(raw) as { error?: { message?: unknown } };
    const message = parsed?.error?.message;
    if (typeof message === 'string' && message.length > 0) return message;
  } catch {
    // Not a JSON body — the raw text is already the message.
  }
  return raw || fallback;
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="bg-gray-900/40 border border-gray-800 rounded-lg p-4">
      <div className="text-[10px] uppercase tracking-wider text-gray-500">{label}</div>
      <div className="text-2xl font-bold text-gray-100 mt-1">{value}</div>
      {hint && <div className="text-xs text-gray-500 mt-1">{hint}</div>}
    </div>
  );
}

function ScopeMeta({ envelope }: { envelope: CommandScopeEnvelope<unknown> | null }) {
  if (!envelope) return null;
  return (
    <div className="text-[10px] text-gray-500 mt-1">
      scope: {envelope.scope.scope_kind} · status: {envelope.status} · basis:{' '}
      {envelope.measurement_basis ?? 'none'} · unit: {envelope.unit?.code ?? envelope.unit?.kind ?? 'none'} ·
      observed_at: {envelope.observed_at ?? '—'}
    </div>
  );
}

function ScopeUnavailable({ label, message }: { label: string; message: string }) {
  return (
    <div className="text-xs">
      <p className="text-red-400">{label} unavailable</p>
      <p className="text-gray-500 mt-1">{message}</p>
    </div>
  );
}

function TrendBars({ trends }: { trends: DailyTrendItem[] }) {
  if (trends.length === 0) return null;
  const max = Math.max(...trends.map((t) => t.total_cost_usd), 0.01);
  return (
    <div className="flex items-end gap-1 h-24">
      {trends.slice(-30).map((t) => {
        const height = Math.max(2, (t.total_cost_usd / max) * 100);
        return (
          <div
            key={t.date}
            title={`${t.date}: $${t.total_cost_usd.toFixed(2)} · ${t.call_count} calls`}
            className="flex-1 bg-[#39FF14]/40 hover:bg-[#39FF14]/70 rounded-t transition-colors"
            style={{ height: `${height}%`, minWidth: 4 }}
          />
        );
      })}
    </div>
  );
}

function usd(amount: number | null | undefined): string {
  return typeof amount === 'number' && Number.isFinite(amount) ? `$${amount.toFixed(2)}` : '—';
}

/**
 * A count the scope did not report is `—`, never `0`.
 *
 * A zero is a value: it says the source counted nothing. A missing field says
 * the source did not report it, and the two must not look alike.
 */
function count(value: number | undefined): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toLocaleString() : '—';
}

export function CostsTab() {
  const [rollup, setRollup] = useState<ScopeRead<RollupData>>({ kind: 'loading' });
  const [summary, setSummary] = useState<ScopeRead<SummaryData>>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;

    Promise.allSettled([
      apiFetch<CommandScopeEnvelope<RollupData>>('/api/v1/costs/rollup?days=30'),
      apiFetch<CommandScopeEnvelope<SummaryData>>('/api/v1/costs/summary?days=30'),
    ]).then(([rollupResult, summaryResult]) => {
      if (cancelled) return;
      setRollup(readScope(rollupResult, 'Failed to load the all-tenants cost rollup'));
      setSummary(readScope(summaryResult, 'Failed to load this tenant’s cost summary'));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const rollupData = rollup.kind === 'facts' ? rollup.envelope.data : null;
  const summaryData = summary.kind === 'facts' ? summary.envelope.data : null;

  return (
    <div className="p-6 font-mono text-sm text-gray-200 space-y-6">
      <div className="flex items-baseline justify-between">
        <div className="text-xs uppercase tracking-wider text-gray-500">Costs · last 30 days</div>
      </div>

      {/* Scope 1 — every tenant. */}
      <section
        className="bg-gray-900/40 border border-gray-800 rounded-lg p-4 space-y-4"
        aria-label="All tenants scope"
      >
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">{ALL_TENANTS_LABEL}</div>
          <ScopeMeta envelope={rollup.kind === 'loading' ? null : rollup.envelope} />
        </div>

        {rollup.kind === 'loading' ? (
          <p className="text-gray-500 text-xs">Loading all-tenants cost data…</p>
        ) : rollup.kind === 'unavailable' ? (
          <ScopeUnavailable label="All tenants" message={rollup.message} />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                label="Total spend"
                value={usd(rollupData?.total_cost_usd)}
                hint={`${rollupData?.days ?? 30}d window`}
              />
              <StatCard label="Calls" value={count(rollupData?.total_calls)} />
              <StatCard label="Tokens" value={count(rollupData?.total_tokens)} />
            </div>

            {rollupData?.period_start && rollupData?.period_end && (
              <div className="text-xs text-gray-500">
                {rollupData.period_start.slice(0, 10)} → {rollupData.period_end.slice(0, 10)}
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">By provider</div>
              {!rollupData || rollupData.by_provider.length === 0 ? (
                <p className="text-gray-500 text-xs">No provider breakdown available.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-gray-500 uppercase tracking-wider">
                    <tr>
                      <th className="text-left py-1 font-normal">Provider</th>
                      <th className="text-right py-1 font-normal">Cost</th>
                      <th className="text-right py-1 font-normal">Calls</th>
                      <th className="text-right py-1 font-normal">Tokens</th>
                      <th className="text-right py-1 font-normal">% of total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...rollupData.by_provider]
                      .sort((a, b) => b.total_cost_usd - a.total_cost_usd)
                      .map((p) => {
                        const pct = rollupData.total_cost_usd > 0
                          ? (p.total_cost_usd / rollupData.total_cost_usd) * 100
                          : 0;
                        return (
                          <tr key={p.provider} className="border-t border-gray-800">
                            <td className="py-2 text-gray-200">{p.provider}</td>
                            <td className="py-2 text-right text-gray-200">${p.total_cost_usd.toFixed(2)}</td>
                            <td className="py-2 text-right text-gray-400">{p.call_count.toLocaleString()}</td>
                            <td className="py-2 text-right text-gray-400">{p.total_tokens.toLocaleString()}</td>
                            <td className="py-2 text-right text-gray-500">{pct.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </section>

      {/* Scope 2 — the caller's own tenant. */}
      <section
        className="bg-gray-900/40 border border-gray-800 rounded-lg p-4 space-y-4"
        aria-label="This tenant scope"
      >
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">{THIS_TENANT_LABEL}</div>
          <ScopeMeta envelope={summary.kind === 'loading' ? null : summary.envelope} />
        </div>

        {summary.kind === 'loading' ? (
          <p className="text-gray-500 text-xs">Loading this tenant’s cost data…</p>
        ) : summary.kind === 'unavailable' ? (
          <ScopeUnavailable label="This tenant" message={summary.message} />
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3">
              <StatCard
                label="Tenant spend"
                value={usd(summaryData?.total_cost_usd)}
                hint={`${summaryData?.days ?? 30}d window`}
              />
              <StatCard label="Avg daily" value={usd(summaryData?.avg_daily_cost_usd)} />
              <StatCard label="Budget" value={usd(summaryData?.budget_limit_usd)} />
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-3">Daily trend</div>
              {summaryData?.daily_trends && summaryData.daily_trends.length > 0 ? (
                <TrendBars trends={summaryData.daily_trends} />
              ) : (
                <p className="text-gray-500 text-xs">No daily trend available.</p>
              )}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
